import { Download, Printer } from "lucide-react";
import { TOOLS } from "@/data/tools";
import { trackDownload } from "@/lib/api";
import { DownloadChoices } from "@/components/DownloadChoices";
import { useActiveSection } from "@/hooks/useStoredState";
import { printTools, scrollToId } from "@/lib/scroll";
import { Reveal } from "@/components/Reveal";
import { Welcome, PrintCover, PageFooterText } from "@/components/tools/Welcome";
import { HealthCheck } from "@/components/tools/HealthCheck";
import { ProvisionReview } from "@/components/tools/ProvisionReview";
import { EhcpChecklist } from "@/components/tools/EhcpChecklist";
import { GovernorQuestions } from "@/components/tools/GovernorQuestions";
import { ParentPartnership } from "@/components/tools/ParentPartnership";
import { WorkloadAudit } from "@/components/tools/WorkloadAudit";
import { ActionPlan } from "@/components/tools/ActionPlan";
import { ResourceDirectory } from "@/components/tools/ResourceDirectory";
import { TermlyReview } from "@/components/tools/TermlyReview";

const LINKS = [{ id: "welcome", n: "—", title: "Welcome" }, ...TOOLS];
const PDF = "/pdfs/sendco-resource-pack.pdf";
const IDS = LINKS.map((t) => t.id);
const STEPS = [
  { n: "1", t: "Choose a section", d: "Use the index in the top bar or the list on the left." },
  { n: "2", t: "Fill it in on screen", d: "Answers save in this browser only. No account, nothing sent." },
  { n: "3", t: "Download or print", d: "Get the blank PDF to complete by hand, or print and save your on-screen answers." },
];

const Index = () => {
  const active = useActiveSection(IDS);
  return (
    <nav aria-label="Sections in this pack" className="no-print sticky top-36 hidden lg:block">
      <p className="eyebrow mb-4">In this pack</p>
      <ol className="space-y-1 border-l-2 border-line">
        {LINKS.map((t) => (
          <li key={t.id}>
            <a href={`#${t.id}`} aria-current={active === t.id ? "true" : undefined} data-testid={`pack-index-${t.id}`}
              onClick={(e) => { e.preventDefault(); scrollToId(t.id); }}
              className={`-ml-0.5 flex gap-3 border-l-2 py-2 pl-4 text-sm font-bold transition-colors ${active === t.id ? "border-brand text-brand" : "border-transparent text-ink-soft hover:text-ink"}`}>
              <span className="w-5 font-display italic">{t.n}</span>{t.title}
            </a>
          </li>
        ))}
      </ol>
      <a href={PDF} download onClick={() => trackDownload("resource-pack-pdf")} className="btn btn-brand btn-sm mt-8" data-testid="download-pack-side">
        <Download size={14} aria-hidden /> Download blank PDF
      </a>
      <button type="button" onClick={() => printTools(null)} className="btn btn-ghost btn-sm mt-3" data-testid="print-pack-side">
        <Printer size={14} aria-hidden /> Print with my answers
      </button>
    </nav>
  );
};

export const ToolkitOne = () => (
  <section id="toolkit-one" aria-labelledby="toolkit-one-title" className="print-scope border-y border-line bg-surface py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">
      <Reveal className="no-print mb-16 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end">
        <div>
          <p className="eyebrow mb-4">Toolkit 02 · Free SENDCO Resource Pack 2026</p>
          <h2 id="toolkit-one-title" className="font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Nine sections.<br /><span className="italic text-brand">Fill in, download, done.</span>
          </h2>
          <DownloadChoices className="mt-8" file="sendco-resource-pack.pdf" resource="resource-pack" label="whole pack (PDF)" testid="download-pack"
            onPrint={() => printTools(null)} onFillOnline={() => scrollToId("welcome")} />
        </div>
        <ol className="grid gap-4 sm:grid-cols-3">
          {STEPS.map((s) => (
            <li key={s.n} className="rounded-2xl border-2 border-line bg-paper p-5">
              <span className="font-display text-3xl italic text-brand" aria-hidden="true">{s.n}</span>
              <p className="mt-2 font-bold">{s.t}</p>
              <p className="mt-1 text-sm text-ink-soft">{s.d}</p>
            </li>
          ))}
        </ol>
      </Reveal>
      <div className="pack-grid grid gap-12 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div><Index /></div>
        <table className="print-table">
          <tfoot><tr><td><PageFooterText /></td></tr></tfoot>
          <tbody><tr><td>
            <PrintCover />
            <div className="space-y-10">
              <Welcome />
              <HealthCheck />
              <ProvisionReview />
              <EhcpChecklist />
              <ParentPartnership />
              <GovernorQuestions />
              <WorkloadAudit />
              <ActionPlan />
              <TermlyReview />
              <ResourceDirectory />
            </div>
          </td></tr></tbody>
        </table>
      </div>
    </div>
  </section>
);
